import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "toggle"]
  static values = {
    maskChar: { type: String, default: "•" },
    revealDuration: { type: Number, default: 1500 },
    maskedIcon: { type: String, default: "fa-eye" },
    visibleIcon: { type: String, default: "fa-eye-slash" },
    autoHideDelay: { type: Number, default: 15000 },
    formatAs: { type: String, default: "" },  // "ssn" (XXX-XX-XXXX) or "ein" (XX-XXXXXXX)
    disablePaste: { type: Boolean, default: false }  // Prevent pasting (for confirmation fields)
  }

  connect() {
    this.isRevealed = false
    this.revealTimeout = null
    this.autoHideTimeout = null
    this.actualValue = this.hiddenTarget.value || ""
    this.lastDisplayLength = this.actualValue.length
    this.isTogglingVisibility = false
    // Track selection range before input for handling selection replacement
    this.selectionStart = 0
    this.selectionEnd = 0

    // Store pattern and remove from element so browser doesn't validate masked bullets
    this.pattern = this.inputTarget.getAttribute('pattern')
    this.patternTitle = this.inputTarget.getAttribute('title') || 'Please match the requested format'
    if (this.pattern) {
      this.inputTarget.removeAttribute('pattern')
    }

    // Expose validation element on hidden field so other controllers can find it
    this.hiddenTarget.maskedInputValidationElement = this.inputTarget

    // Initialize display
    this.updateDisplay()
    this.updateToggleIcon()

    // Run initial validation
    this.validatePattern()

    // Apply is-invalid class if validation fails on connect
    if (this.actualValue && !this.inputTarget.checkValidity()) {
      this.inputTarget.classList.add('is-invalid')
    }

    // Auto-bind all input target events so views don't need explicit data-action bindings
    this.inputHandler = this.onInput.bind(this)
    this.inputTarget.addEventListener('input', this.inputHandler)

    this.blurHandler = this.onBlur.bind(this)
    this.inputTarget.addEventListener('blur', this.blurHandler)

    this.keydownHandler = this.onKeydown.bind(this)
    this.inputTarget.addEventListener('keydown', this.keydownHandler)

    this.pasteHandler = this.onPaste.bind(this)
    this.inputTarget.addEventListener('paste', this.pasteHandler)

    // Auto-bind toggle button events if toggle target exists
    if (this.hasToggleTarget) {
      this.toggleMousedownHandler = this.toggleMousedown.bind(this)
      this.toggleTarget.addEventListener('mousedown', this.toggleMousedownHandler)

      this.toggleClickHandler = this.toggle.bind(this)
      this.toggleTarget.addEventListener('click', this.toggleClickHandler)
    }

    // Listen for Turbo morphing to refresh display after DOM updates
    this.morphHandler = () => {
      // Don't reset state if user is currently typing in this field
      // This prevents auto-save morphs from clobbering user input mid-keystroke
      if (document.activeElement === this.inputTarget) {
        return
      }

      // Re-read value from hidden field and update display
      this.actualValue = this.hiddenTarget.value || ""
      this.lastDisplayLength = this.actualValue.length
      this.updateDisplay()
      this.validatePattern()
    }
    this.element.addEventListener('turbo:morph-element', this.morphHandler)
  }

  disconnect() {
    if (this.revealTimeout) {
      clearTimeout(this.revealTimeout)
    }
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout)
    }

    // Clean up input target event listeners
    if (this.inputHandler) {
      this.inputTarget.removeEventListener('input', this.inputHandler)
    }
    if (this.blurHandler) {
      this.inputTarget.removeEventListener('blur', this.blurHandler)
    }
    if (this.keydownHandler) {
      this.inputTarget.removeEventListener('keydown', this.keydownHandler)
    }
    if (this.pasteHandler) {
      this.inputTarget.removeEventListener('paste', this.pasteHandler)
    }

    // Clean up toggle target event listeners
    if (this.toggleMousedownHandler && this.hasToggleTarget) {
      this.toggleTarget.removeEventListener('mousedown', this.toggleMousedownHandler)
    }
    if (this.toggleClickHandler && this.hasToggleTarget) {
      this.toggleTarget.removeEventListener('click', this.toggleClickHandler)
    }

    if (this.morphHandler) {
      this.element.removeEventListener('turbo:morph-element', this.morphHandler)
    }
  }

  // Capture selection before input to handle selection replacement correctly
  onKeydown(event) {
    this.selectionStart = this.inputTarget.selectionStart
    this.selectionEnd = this.inputTarget.selectionEnd
  }

  onInput(event) {
    const input = this.inputTarget
    const cursorPos = input.selectionStart
    const displayValue = input.value

    if (this.isRevealed) {
      // If revealed, just update actual value directly
      this.actualValue = displayValue
      this.hiddenTarget.value = this.actualValue
      this.lastDisplayLength = this.actualValue.length

      // Validate against pattern if present (must run before dispatch so checkValidity works)
      this.validatePattern()

      // Dispatch input event on hidden field so other controllers can listen
      this.hiddenTarget.dispatchEvent(new Event('input', { bubbles: true }))

      // Reset auto-hide timer when typing in revealed mode
      if (this.autoHideTimeout) {
        clearTimeout(this.autoHideTimeout)
        this.autoHideTimeout = setTimeout(() => {
          this.isRevealed = false
          this.updateDisplay()
          this.updateToggleIcon()
        }, this.autoHideDelayValue)
      }
      return
    }

    const newLength = displayValue.length
    const oldLength = this.lastDisplayLength
    const hadSelection = this.selectionStart !== this.selectionEnd

    if (hadSelection) {
      // Selection was replaced - handle delete + insert in one operation
      // The typed/pasted characters replace the selected range
      const selectedCount = this.selectionEnd - this.selectionStart
      const insertedCount = newLength - (oldLength - selectedCount)
      const insertedChars = displayValue.slice(cursorPos - insertedCount, cursorPos)

      this.actualValue =
        this.actualValue.slice(0, this.selectionStart) +
        insertedChars +
        this.actualValue.slice(this.selectionEnd)
    } else if (newLength > oldLength) {
      // Characters added (no selection)
      const addedCount = newLength - oldLength
      const addedChars = displayValue.slice(cursorPos - addedCount, cursorPos)
      const insertPos = cursorPos - addedCount

      this.actualValue =
        this.actualValue.slice(0, insertPos) +
        addedChars +
        this.actualValue.slice(insertPos)
    } else if (newLength < oldLength) {
      // Characters deleted (no selection)
      const deletedCount = oldLength - newLength
      this.actualValue =
        this.actualValue.slice(0, cursorPos) +
        this.actualValue.slice(cursorPos + deletedCount)
    }

    // Reset selection tracking
    this.selectionStart = 0
    this.selectionEnd = 0

    // Update tracking
    this.lastDisplayLength = this.actualValue.length
    this.hiddenTarget.value = this.actualValue

    // Validate against pattern if present (must run before dispatch so checkValidity works)
    this.validatePattern()

    // Dispatch input event on hidden field so other controllers can listen
    this.hiddenTarget.dispatchEvent(new Event('input', { bubbles: true }))

    // Show last character briefly, then mask
    this.showLastCharBriefly(cursorPos)
  }

  validatePattern() {
    if (!this.pattern) return

    const regex = new RegExp(`^${this.pattern}$`)
    if (this.actualValue === '') {
      // Empty value - clear validation error but don't touch is-invalid class
      // (backend controls is-invalid for empty fields)
      this.inputTarget.setCustomValidity('')
    } else if (regex.test(this.actualValue)) {
      // Valid value - clear error and remove is-invalid
      this.inputTarget.setCustomValidity('')
      this.inputTarget.classList.remove('is-invalid')
    } else {
      // Invalid value - set error and add is-invalid
      this.inputTarget.setCustomValidity(this.patternTitle)
      this.inputTarget.classList.add('is-invalid')
    }
  }

  showLastCharBriefly(cursorPos) {
    if (this.revealTimeout) {
      clearTimeout(this.revealTimeout)
    }

    // Show with last typed char visible (at cursor position - 1)
    const masked = this.getMaskedWithCharVisible(cursorPos - 1)
    this.inputTarget.value = masked
    // Note: Don't update lastDisplayLength here - it should only track actualValue.length
    // The display value changes (masked/partially masked) but actualValue stays consistent

    // Restore cursor position
    this.inputTarget.setSelectionRange(cursorPos, cursorPos)

    // After delay, fully mask
    this.revealTimeout = setTimeout(() => {
      const currentPos = this.inputTarget.selectionStart
      this.updateDisplay()
      this.inputTarget.setSelectionRange(currentPos, currentPos)
    }, this.revealDurationValue)
  }

  getMaskedWithCharVisible(visibleIndex) {
    if (this.actualValue.length === 0) return ""
    if (visibleIndex < 0 || visibleIndex >= this.actualValue.length) {
      return this.getFullyMasked()
    }

    let result = ""
    for (let i = 0; i < this.actualValue.length; i++) {
      result += (i === visibleIndex) ? this.actualValue[i] : this.maskCharValue
    }
    return result
  }

  getFullyMasked() {
    return this.maskCharValue.repeat(this.actualValue.length)
  }

  updateDisplay() {
    if (this.isRevealed) {
      this.inputTarget.value = this.formatForDisplay(this.actualValue)
      // Restore pattern when revealed so native validation works
      if (this.pattern) {
        this.inputTarget.setAttribute('pattern', this.pattern)
      }
    } else {
      this.inputTarget.value = this.getFullyMasked()
      // Remove pattern when masked so bullets don't fail validation
      if (this.pattern) {
        this.inputTarget.removeAttribute('pattern')
      }
    }
    // Note: Don't update lastDisplayLength here - it should only track actualValue.length
    // This method just changes the display, not the actual value
  }

  // Format value for display when revealed (e.g., SSN as XXX-XX-XXXX)
  formatForDisplay(value) {
    if (!this.formatAsValue || !value) return value

    // Strip all non-digits
    const digits = value.replace(/\D/g, '')

    // Only format if exactly 9 digits
    if (digits.length !== 9) return value

    if (this.formatAsValue === 'ssn') {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`
    } else if (this.formatAsValue === 'ein') {
      return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`
    }

    return value
  }

  // Called on mousedown to set flag before blur fires
  toggleMousedown() {
    this.isTogglingVisibility = true
  }

  toggle() {
    // Clear the flag after a short delay
    setTimeout(() => { this.isTogglingVisibility = false }, 100)

    this.isRevealed = !this.isRevealed
    this.updateDisplay()
    this.updateToggleIcon()

    // Clear any existing auto-hide timeout
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout)
      this.autoHideTimeout = null
    }

    // Start auto-hide timer when revealing
    if (this.isRevealed && this.autoHideDelayValue > 0) {
      this.autoHideTimeout = setTimeout(() => {
        this.isRevealed = false
        this.updateDisplay()
        this.updateToggleIcon()
      }, this.autoHideDelayValue)
    }

    // Refocus the input after toggle
    this.inputTarget.focus()
  }

  updateToggleIcon() {
    if (!this.hasToggleTarget) return

    // FontAwesome SVG+JS replaces <i> with <svg>, so we need to replace the whole icon
    const iconClass = this.isRevealed ? this.visibleIconValue : this.maskedIconValue
    this.toggleTarget.innerHTML = `<i class="fas ${iconClass}"></i>`
  }

  onBlur() {
    // Skip if we're in the middle of toggling visibility
    if (this.isTogglingVisibility) return

    // Always mask when field loses focus
    if (this.revealTimeout) {
      clearTimeout(this.revealTimeout)
    }
    this.isRevealed = false
    this.updateDisplay()
    this.updateToggleIcon()
  }

  // Handle paste
  onPaste(event) {
    // Block paste entirely if disabled (for confirmation fields)
    if (this.disablePasteValue) {
      event.preventDefault()
      return
    }

    if (this.isRevealed) return // Let default behavior handle it

    event.preventDefault()
    const pastedText = (event.clipboardData || window.clipboardData).getData('text')
    const input = this.inputTarget
    const cursorPos = input.selectionStart
    const selectionEnd = input.selectionEnd

    // Replace selection with pasted text
    this.actualValue =
      this.actualValue.slice(0, cursorPos) +
      pastedText +
      this.actualValue.slice(selectionEnd)
    this.hiddenTarget.value = this.actualValue

    // Validate against pattern if present (must run before dispatch so checkValidity works)
    this.validatePattern()

    // Dispatch input event on hidden field so other controllers can listen
    this.hiddenTarget.dispatchEvent(new Event('input', { bubbles: true }))

    // Show last char of paste briefly then mask
    const newCursorPos = cursorPos + pastedText.length
    this.showLastCharBriefly(newCursorPos)
  }
}
